import type { EventCalendarView } from '@all-manager/design-system-v2';
import { lightTheme } from '@all-manager/design-system-v2/theme';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSpacePath } from '../../../../hooks/useSpacePath';
import { useGetCalendarItems } from '../../api/useGetCalendarItems';
import { SelectedCalendarItem } from '../../types/calendar';
import { getCalendarRange, toCalendarEvents } from '../../utils/calendarEvents';

const PHONE_QUERY = lightTheme.breakpoints.down('sm').replace(/^@media\s*/, '');

// On a phone the month grid truncates every title: the agenda lists them in full.
function getInitialView(): EventCalendarView {
  return window.matchMedia(PHONE_QUERY).matches ? 'agenda' : 'month';
}

export function useCalendarPlanning() {
  const navigate = useNavigate();
  const spacePath = useSpacePath();

  const [view, setView] = useState<EventCalendarView>(getInitialView);
  const [visibleDate, setVisibleDate] = useState(() => new Date());
  const [selected, setSelected] = useState<SelectedCalendarItem | null>(null);

  const range = useMemo(() => getCalendarRange(view, visibleDate), [view, visibleDate]);
  const { data, isFetching, isError, refetch } = useGetCalendarItems(range.minDate, range.maxDate);

  const items = useMemo(() => data ?? [], [data]);
  const events = useMemo(() => toCalendarEvents(items), [items]);

  function handleEventClick(eventId: string, anchor: HTMLElement | null) {
    const item = items.find((candidate) => candidate.id === eventId);

    if (!item) {
      return;
    }

    if (item.type === 'council_meeting') {
      navigate(spacePath('/council/meetings'));
      return;
    }

    setSelected({ item, anchor });
  }

  function handleCloseDetail() {
    setSelected(null);
  }

  function handleRetry() {
    refetch();
  }

  return {
    view,
    visibleDate,
    events,
    selected,
    isFetching,
    isError,
    setView,
    setVisibleDate,
    handleEventClick,
    handleCloseDetail,
    handleRetry,
  };
}
